// src/components/OrgChartView.jsx
//
// Phase O — Internal org chart directory. Reporting hierarchy with
// collapsible branches, direct-report counts, search, and CSV import.
//
// Permissions:
//   - View: all authenticated roles except deal_advisor (enforced by RLS)
//   - Edit/import: admin only (enforced by RLS + UI gating)

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Users, Search, Loader2, Plus, Upload, Pencil, Trash2, X,
  ZoomIn, ZoomOut, Crosshair, ChevronDown, ChevronUp,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Helpers for import: header aliasing + title casing
// ---------------------------------------------------------------------------

// Maps lots of common spreadsheet headers to our canonical field names.
// Anything not in this map is kept as-is so users can also import using
// our native column names (name, manager_email, etc.).
const HEADER_ALIASES = {
  // Identity
  'name':                'name',
  'legal_name':          'name',
  'full_name':           'name',
  'employee_name':       'name',
  'preferred_name':      'name',
  // Title
  'title':               'title',
  'job_title':           'title',
  'job_title_description': 'title',
  'position':            'title',
  'role':                'title',
  // Email
  'email':               'email',
  'email_address':       'email',
  'work_email':          'email',
  // Phone
  'phone':               'phone',
  'cell':                'phone',
  'cell_phone':          'phone',
  'mobile':              'phone',
  'mobile_phone':        'phone',
  'phone_number':        'phone',
  'work_phone':          'phone',
  // Department / business unit
  'department':          'department',
  'dept':                'department',
  'business_unit':       'department',
  'business_unit_description': 'department',
  'division':            'department',
  // Location
  'location':            'location',
  'location_description': 'location',
  'office':              'location',
  'city':                'location',
  // Manager
  'manager':             'manager_name',
  'manager_name':        'manager_name',
  'reports_to':          'manager_name',
  'reports_to_legal_name': 'manager_name',
  'reports_to_name':     'manager_name',
  'supervisor':          'manager_name',
  'manager_email':       'manager_email',
  'reports_to_email':    'manager_email',
  // Photo
  'photo_url':           'photo_url',
  'photo':               'photo_url',
  'avatar':              'photo_url',
  'avatar_url':          'photo_url',
  // Employment category — full time / part time / contractor / etc.
  // Part-time rows are filtered out during import (Phase O.8).
  'category':            'category',
  'employment_type':     'category',
  'employment':          'category',
  'employment_status':   'category',
  'status':              'category',
  'type':                'category',
};

const CANONICAL_FIELDS = new Set(['name','title','email','phone','department','location','manager_name','manager_email','photo_url','notes','category']);

// Title-case a string for HR-style display. Handles:
//   - Mc / Mac prefixes (McDonald, MacKenzie)
//   - Roman numerals (II, III, IV, etc.) → all caps
//   - Suffixes Jr / Sr → keep, with trailing period preserved
//   - Hyphens and apostrophes preserved with capitalization on both sides
//   - All-caps tokens like "EMP" stay all-caps if they were uppercase in source
//     AND short enough to be an acronym (≤4 chars, no vowels-only)
// Reasonable default; not perfect for every edge case.
function titleCase(input) {
  if (input == null) return input;
  const s = String(input).trim();
  if (!s) return s;

  const ROMAN = /^(ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)$/i;
  const SUFFIX_PERIOD = /^(jr|sr)\.?$/i;

  const tokens = s.split(/(\s+|-|')/); // keep separators so we can rejoin cleanly
  return tokens.map(tok => {
    if (!tok || /^\s+$/.test(tok) || tok === '-' || tok === "'") return tok;
    // Roman numerals
    if (ROMAN.test(tok)) return tok.toUpperCase();
    // Jr / Sr with optional period
    const sufMatch = tok.match(SUFFIX_PERIOD);
    if (sufMatch) return tok[0].toUpperCase() + tok.slice(1, sufMatch[0].length).toLowerCase() + (tok.endsWith('.') ? '' : '');
    // Mc / Mac prefix
    const mc = tok.match(/^mc([a-z])(.*)$/i);
    if (mc) return 'Mc' + mc[1].toUpperCase() + mc[2].toLowerCase();
    const mac = tok.match(/^mac([a-z])(.*)$/i);
    // Only treat as Mac prefix if word is long enough that it's clearly not "mac" the word
    if (mac && tok.length >= 5) return 'Mac' + mac[1].toUpperCase() + mac[2].toLowerCase();
    // Short ALL-CAPS token (likely acronym) — preserve
    if (tok === tok.toUpperCase() && tok.length <= 4 && /^[A-Z]+$/.test(tok)) return tok;
    // Generic: lowercase, then capitalize first letter
    const lower = tok.toLowerCase();
    return lower[0].toUpperCase() + lower.slice(1);
  }).join('');
}

// Title-case but lowercase email entirely (case-insensitive standard).
function normalizeRow(raw) {
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const aliased = HEADER_ALIASES[k] || k;
    if (!CANONICAL_FIELDS.has(aliased)) continue; // ignore stray columns
    const val = (v == null ? '' : String(v)).trim();
    if (!val) continue;
    if (aliased === 'email' || aliased === 'manager_email') {
      out[aliased] = val.toLowerCase();
    } else if (aliased === 'photo_url') {
      out[aliased] = val; // URL as-is
    } else {
      out[aliased] = titleCase(val);
    }
  }
  return out;
}

function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '?';
}

// HR data is stored "Last, First" (the CSV format the import expects so
// manager_name matching stays consistent). For display we flip to
// "First Last" because that's how people actually read names.
function displayName(stored) {
  if (!stored) return stored;
  const trimmed = String(stored).trim();
  const m = trimmed.match(/^([^,]+),\s*(.+)$/);
  if (m) return `${m[2].trim()} ${m[1].trim()}`;
  return trimmed;
}

// Pastel deterministic color from name — for the avatar fallback.
function colorFor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = ((h << 5) - h) + name.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 88%)`;
}
function textColorFor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = ((h << 5) - h) + name.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 65%, 30%)`;
}

// Normalize a header string into a key suitable for HEADER_ALIASES lookup:
// lowercase, alphanumeric + underscores, spaces and slashes collapsed.
function headerKey(h) {
  return String(h || '').trim().toLowerCase()
    .replace(/[\/\\]+/g, '_')
    .replace(/[^a-z0-9_\s]/g, '')
    .replace(/\s+/g, '_');
}

// CSV parser — handles quoted fields with commas + escaped double-quotes.
// Returns array of objects keyed by normalized header.
function parseCsv(text) {
  const out = [];
  let lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.length);
  if (lines.length < 2) return out;
  const parseLine = (line) => {
    const cells = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else cur += c;
      } else {
        if (c === ',') { cells.push(cur); cur = ''; }
        else if (c === '"') inQ = true;
        else cur += c;
      }
    }
    cells.push(cur);
    return cells;
  };
  const headers = parseLine(lines[0]).map(headerKey);
  for (let i = 1; i < lines.length; i++) {
    const cells = parseLine(lines[i]);
    if (cells.every(c => !c.trim())) continue;
    const row = {};
    headers.forEach((h, idx) => { row[h] = (cells[idx] || '').trim(); });
    out.push(row);
  }
  return out;
}

// XLSX parser via SheetJS. Reads the first sheet, returns the same
// shape as parseCsv (array of objects keyed by normalized header).
async function parseXlsx(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];
  // header:1 gives us a 2D array; first row = headers
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
  if (!aoa.length) return [];
  const headers = aoa[0].map(headerKey);
  const out = [];
  for (let i = 1; i < aoa.length; i++) {
    const cells = aoa[i] || [];
    if (cells.every(c => String(c).trim() === '')) continue;
    const row = {};
    headers.forEach((h, idx) => { row[h] = String(cells[idx] ?? '').trim(); });
    out.push(row);
  }
  return out;
}

export default function OrgChartView({ organization, isAdmin }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editPerson, setEditPerson] = useState(null);    // null | person object | 'new'
  const [showImport, setShowImport] = useState(false);
  // Hide-part-time toggle. Filter is non-destructive (data stays in DB);
  // toggle just hides rows whose category contains "part" (case-insensitive).
  const [hidePartTime, setHidePartTime] = useState(false);
  const [stateFilter, setStateFilter] = useState('all');   // 'all' | 'OH' | 'PA' | ... | 'remote'
  // When set, the canvas pans to bring that person into view on the next
  // layout pass. Cleared after centering. `anchor` controls vertical
  // placement: 'top' for expand actions (so children below are visible),
  // 'center' for search picks.
  const [centerTarget, setCenterTarget] = useState(null);  // { id, anchor } | null
  // Track whether the initial collapse-everything-below-direct-reports
  // setup has run yet, so manual toggles don't get clobbered on re-render.
  const initialCollapseDoneRef = useRef(false);

  const isPartTime = (p) => p.category && /part[\s\-]?time|^pt$/i.test(p.category);

  const statesPresent = useMemo(() => {
    const s = new Set();
    for (const p of people) if (p.state) s.add(p.state);
    return Array.from(s).sort();
  }, [people]);
  const hasRemote = useMemo(() =>
    people.some(p => !p.state && (p.city || '').toLowerCase() === 'remote'),
    [people]
  );

  const refresh = async () => {
    if (!organization?.id) return;
    setLoading(true); setError(null);
    try {
      const { data, error: err } = await supabase
        .from('org_chart_people')
        .select('id, name, title, email, phone, department, location, state, city, photo_url, manager_id, sort_order, notes, category, is_active')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('sort_order')
        .order('name');
      if (err) throw err;
      setPeople(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [organization?.id]);

  // Apply hide-part-time + state filter BEFORE building the tree.
  // If a manager is filtered out, their reports re-parent to the next
  // visible ancestor so the tree stays continuous.
  const visiblePeople = useMemo(() => {
    const matchesState = (p) => {
      if (stateFilter === 'all') return true;
      if (stateFilter === 'remote') return !p.state && (p.city || '').toLowerCase() === 'remote';
      return p.state === stateFilter;
    };
    const matchesPt = (p) => !hidePartTime || !isPartTime(p);
    const visible = (p) => matchesState(p) && matchesPt(p);
    if (people.every(visible)) return people;

    const hiddenIds = new Set(people.filter(p => !visible(p)).map(p => p.id));
    const byId = {}; for (const p of people) byId[p.id] = p;
    const resolveManager = (mgrId) => {
      let cur = mgrId;
      while (cur && hiddenIds.has(cur)) {
        cur = byId[cur]?.manager_id || null;
      }
      return cur;
    };
    return people
      .filter(visible)
      .map(p => ({ ...p, manager_id: resolveManager(p.manager_id) }));
  }, [people, hidePartTime, stateFilter]);

  // Index: id → person, manager_id → children
  const { byId, childrenOf, roots, reportCount } = useMemo(() => {
    const m = {}; const kids = {};
    for (const p of visiblePeople) {
      m[p.id] = p;
      (kids[p.manager_id || '__root__'] ||= []).push(p);
    }
    const sortFn = (a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name);
    Object.values(kids).forEach(arr => arr.sort(sortFn));
    const r = kids['__root__'] || [];

    // Direct report count (and total descendants) per id
    const direct = {};
    for (const p of visiblePeople) direct[p.id] = (kids[p.id] || []).length;
    return { byId: m, childrenOf: (id) => kids[id || '__root__'] || [], roots: r, reportCount: direct };
  }, [visiblePeople]);

  // Initial state: collapse every node EXCEPT roots. That means roots and
  // their direct reports are visible, but grandchildren and below start
  // hidden. Subsequent badge clicks expand one level at a time.
  useEffect(() => {
    if (initialCollapseDoneRef.current) return;
    if (visiblePeople.length === 0) return;
    const initial = new Set();
    const rootIds = new Set(roots.map(r => r.id));
    for (const p of visiblePeople) {
      if ((reportCount[p.id] || 0) > 0 && !rootIds.has(p.id)) {
        initial.add(p.id);
      }
    }
    setCollapsed(initial);
    initialCollapseDoneRef.current = true;
  }, [visiblePeople, roots, reportCount]);

  // Search: returns the ordered list of matches + the set of ancestors
  // to force-expand so paths to matches are visible.
  const searchResult = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const matches = [];
    for (const p of visiblePeople) {
      const hay = `${p.name} ${p.title || ''} ${p.department || ''} ${p.email || ''} ${p.city || ''} ${p.state || ''}`.toLowerCase();
      if (hay.includes(q)) matches.push(p);
    }
    const matchIds = new Set(matches.map(m => m.id));
    const expanded = new Set();
    for (const id of matchIds) {
      let cur = byId[id];
      while (cur?.manager_id) {
        expanded.add(cur.manager_id);
        cur = byId[cur.manager_id];
      }
    }
    return { matches, matchIds, expanded };
  }, [search, visiblePeople, byId]);

  // Single-branch rule: only one sibling at a given tier can be expanded
  // at a time. Expanding node X collapses X's siblings (nodes with the
  // same parent) so the user only ever sees one vertical branch beneath
  // a given manager. Also recenters the canvas on the expanded manager.
  const toggleCollapsed = (id) => {
    const isCurrentlyCollapsed = collapsed.has(id);
    if (isCurrentlyCollapsed) {
      // EXPAND: also collapse this node's siblings
      const target = visiblePeople.find(p => p.id === id);
      const siblingIds = target
        ? visiblePeople
            .filter(p => p.id !== id && p.manager_id === target.manager_id)
            .map(p => p.id)
        : [];
      setCollapsed(prev => {
        const next = new Set(prev);
        next.delete(id);
        for (const sibId of siblingIds) next.add(sibId);
        return next;
      });
      setCenterTarget({ id, anchor: 'top' });
    } else {
      // COLLAPSE: just close this node, leave siblings alone
      setCollapsed(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
  };

  const isCollapsed = (id) => {
    if (searchResult && searchResult.expanded.has(id)) return false;  // force open path to a match
    return collapsed.has(id);
  };

  // Edge-to-edge layout: thin top bar with title + toolbar, then the
  // tree fills the entire remaining viewport. The parent <main> has
  // position:relative so absolute positioning fills it correctly.
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
      <div style={{
        padding: '10px 20px 10px 56px',     // 56px on left leaves room for the sidebar toggle handle
        borderBottom: '1px solid #E5E9F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexShrink: 0,
        backgroundColor: '#fff',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Users className="w-5 h-5" style={{ color: '#1A3665', flexShrink: 0 }} />
          <h1 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: 0, letterSpacing: '-0.01em' }}>Org chart</h1>
          <span style={{ fontSize: 12, color: '#94A3B8', whiteSpace: 'nowrap' }}>
            {people.length} {people.length === 1 ? 'person' : 'people'} · drag to pan · scroll to zoom · click count badges to expand
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox
            search={search}
            setSearch={setSearch}
            results={searchResult?.matches || []}
            onPick={(p) => {
              setSearch('');
              // Walk up the chain from p to the root, expanding each
              // ancestor and collapsing every sibling of each node
              // along the path (single-branch rule).
              setCollapsed(prev => {
                const next = new Set(prev);
                next.delete(p.id);
                let cur = byId[p.id];
                while (cur) {
                  // Collapse siblings of cur
                  const mgrId = cur.manager_id;
                  for (const other of visiblePeople) {
                    if (other.id !== cur.id && other.manager_id === mgrId) {
                      next.add(other.id);
                    }
                  }
                  if (!mgrId) break;
                  next.delete(mgrId);   // expand parent
                  cur = byId[mgrId];
                }
                return next;
              });
              setCenterTarget({ id: p.id, anchor: 'center' });
            }}
          />
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            className="h-8 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
            title="Filter by state"
          >
            <option value="all">All states</option>
            {statesPresent.map(s => <option key={s} value={s}>{s}</option>)}
            {hasRemote && <option value="remote">Remote</option>}
          </select>
          <label className="h-8 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 cursor-pointer"
                 style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                 title="Hide anyone whose category contains 'part' (PartTime, Part-Time, etc.)">
            <input type="checkbox" checked={hidePartTime} onChange={e => setHidePartTime(e.target.checked)} className="cursor-pointer" />
            Hide part-time
          </label>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="h-8 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 hover:opacity-80 cursor-pointer"
                style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
              >
                <Upload className="w-3.5 h-3.5" /> Import
              </button>
              <button
                type="button"
                onClick={() => setEditPerson('new')}
                className="h-8 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 hover:opacity-90 cursor-pointer"
                style={{ backgroundColor: '#1A3665', color: 'white' }}
              >
                <Plus className="w-3.5 h-3.5" /> Add person
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div style={{ margin: '8px 20px', padding: '8px 12px', backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, color: '#B91C1C', flexShrink: 0 }}>
          {error}
        </div>
      )}

      <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748B' }}>
            <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: '#1A3665' }} />
            <p className="text-sm">Loading org chart…</p>
          </div>
        ) : people.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Users className="w-10 h-10 mb-3" style={{ color: '#CBD5E1' }} />
            <p className="text-sm font-medium" style={{ color: '#475569' }}>No org chart data yet</p>
            {isAdmin && (
              <p className="text-[13px] mt-1" style={{ color: '#94A3B8' }}>
                Start by clicking <strong>Add person</strong> or <strong>Import</strong>.
              </p>
            )}
          </div>
        ) : (
          <OrgTreeCanvas
            roots={roots}
            childrenOf={childrenOf}
            reportCount={reportCount}
            isCollapsed={isCollapsed}
            toggleCollapsed={toggleCollapsed}
            isAdmin={isAdmin}
            onEdit={setEditPerson}
            searchMatches={searchResult?.matchIds}
            centerTarget={centerTarget}
            onCenterDone={() => setCenterTarget(null)}
          />
        )}
      </div>

      {editPerson && (
        <EditPersonModal
          organization={organization}
          person={editPerson === 'new' ? null : editPerson}
          people={people}
          onClose={() => setEditPerson(null)}
          onSaved={() => { setEditPerson(null); refresh(); }}
        />
      )}
      {showImport && (
        <ImportCsvModal
          organization={organization}
          onClose={() => setShowImport(false)}
          onComplete={() => { setShowImport(false); refresh(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OrgTreeCanvas — Pingboard-style horizontal org chart.
//
// Layout uses the classic CSS "flexbox + pseudo-element connectors"
// technique so we get clean horizontal trees with elbow connectors
// without any layout-math JS. The whole canvas sits inside an
// overflow:auto container so wide trees pan horizontally.
//
// Collapse/expand affordance is the blue direct-report count badge
// on each card — clicking it toggles the subtree below.
// ---------------------------------------------------------------------------

const ORG_TREE_CSS = `
.org-canvas {
  position: relative;
  overflow: hidden;
  background: #FAFBFD;
  width: 100%;
  height: 100%;
  cursor: grab;
  user-select: none;
}
.org-canvas.is-panning { cursor: grabbing; }
.org-canvas-inner {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  padding: 48px 60px;
  will-change: transform;
}
.org-tree li { padding: 0 6px; }
/* Layout-only spacing; connectors come from the SVG overlay below. */
.org-tree ul { padding-top: 44px; }
.org-zoom-toolbar {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  gap: 4px;
  background: #fff;
  border: 1px solid #E5E9F0;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(15,23,42,0.08);
  z-index: 2;
}
.org-zoom-toolbar button {
  background: none;
  border: none;
  padding: 6px 8px;
  cursor: pointer;
  color: #475569;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
}
.org-zoom-toolbar button:hover { background: #F0F2F6; color: #0F172A; }
.org-zoom-toolbar .zoom-level { font-family: ui-monospace, monospace; font-size: 11px; color: #64748B; padding: 0 4px; align-self: center; }
.org-tree, .org-tree ul {
  display: inline-flex;
  padding: 0;
  margin: 0;
  list-style: none;
  position: relative;
}
.org-tree ul {
  padding-top: 32px;
  justify-content: center;
}
.org-tree li {
  padding: 0 12px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}
/* Card-wrap is positioned-relative so the chevron button can sit absolute
   inside it. No CSS connector lines — those come from the SVG overlay. */
.org-tree-card-wrap { position: relative; }
.org-toggle-btn {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid #94A3B8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #475569;
  z-index: 2;
  padding: 0;
  transition: border-color 120ms, background 120ms, color 120ms;
}
.org-toggle-btn:hover {
  border-color: #1A3665;
  background: #EFF4FB;
  color: #1A3665;
}
.org-toggle-btn.is-collapsed { border-color: #CBD5E1; }
.org-toggle-btn.is-collapsed:hover { border-color: #1A3665; }
.org-card {
  width: 210px;
  background: #fff;
  border: 1px solid #E5E9F0;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
  position: relative;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  transition: box-shadow 150ms, border-color 150ms, transform 150ms;
}
.org-card:hover { box-shadow: 0 4px 12px rgba(15,23,42,0.08); border-color: #CBD5E1; transform: translateY(-1px); }
.org-card.is-match { background: #FEFCE8; border-color: #FDE68A; }
.org-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  background-size: cover;
  background-position: center;
  border: 1px solid #E5E9F0;
}
.org-card-body { flex: 1; min-width: 0; }
.org-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #0F172A;
  line-height: 1.3;
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-card-title {
  font-size: 12px;
  color: #475569;
  margin-top: 3px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-card-meta {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 6px;
  line-height: 1.35;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-edit-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: none;
  border: none;
  padding: 3px;
  cursor: pointer;
  color: #CBD5E1;
  opacity: 0;
  transition: opacity 120ms, color 120ms;
  border-radius: 4px;
}
.org-card:hover .org-edit-btn { opacity: 1; }
.org-edit-btn:hover { color: #1A3665; background: #F0F2F6; }
`;

function OrgTreeCanvas({ roots, childrenOf, reportCount, isCollapsed, toggleCollapsed, isAdmin, onEdit, searchMatches, centerTarget, onCenterDone }) {
  const canvasRef = useRef(null);
  const innerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 80, y: 40 });
  const [panning, setPanning] = useState(false);
  const panStartRef = useRef(null);

  // --- Custom tree layout ---
  // Cards are absolute-positioned via a per-tier pass so cousin subtrees
  // CAN overlap horizontally. The standard Reingold-Tilford layout pushes
  // siblings apart by subtree breadth, which makes one wide branch
  // exile its leaf-sibling far to the right. We accept cousin overlap
  // in exchange for a compact chart — leaf-siblings stay near the parent
  // and the wide branch's children extend below them.
  const CARD_W = 210;
  const CARD_H = 96;
  const TIER_GAP = 56;  // vertical gap between tiers (in addition to card height)
  const SIB_GAP  = 16;  // horizontal gap between sibling cards at same tier

  const layout = useMemo(() => {
    const pos = {};                 // id → { x, y }
    const personMap = {};           // id → person object
    const TIER = CARD_H + TIER_GAP;
    let bounds = { minX: 0, maxX: 0, maxY: 0 };

    // Pure recursive centering. Each subtree is independent:
    //   - Parent placed at a given center X
    //   - Children placed centered under parent (evenly spaced)
    //   - Recurses into each child with that child's center
    // Pros: every parent is perfectly centered over its visible children,
    // and adding/removing descendants never shifts ancestors or siblings.
    // Cons: cousin subtrees can overlap in X when both are expanded wide.
    // That tradeoff is accepted — the chart is meant to be skimmed one
    // branch at a time, with curves disambiguating relationships.
    const place = (node, depth, centerX) => {
      personMap[node.id] = node;
      const y = depth * TIER;
      const x = centerX - CARD_W / 2;
      pos[node.id] = { x, y };
      bounds.minX = Math.min(bounds.minX, x);
      bounds.maxX = Math.max(bounds.maxX, x + CARD_W);
      bounds.maxY = Math.max(bounds.maxY, y + CARD_H);

      if (isCollapsed(node.id)) return;
      const kids = childrenOf(node.id);
      if (kids.length === 0) return;

      const childSpan = kids.length * CARD_W + (kids.length - 1) * SIB_GAP;
      const firstChildCenter = centerX - childSpan / 2 + CARD_W / 2;
      for (let i = 0; i < kids.length; i++) {
        const childCenter = firstChildCenter + i * (CARD_W + SIB_GAP);
        place(kids[i], depth + 1, childCenter);
      }
    };

    // Place each root at center=0 (or staggered if multiple roots)
    for (let i = 0; i < roots.length; i++) {
      const rootCenter = i === 0 ? 0 : bounds.maxX + CARD_W / 2 + 80;
      place(roots[i], 0, rootCenter);
    }

    // Recursive centering produces negative X coords (children extend
    // left of their parent). Translate everything right so the leftmost
    // card sits at a small positive padding.
    const padding = 40;
    const offset = padding - bounds.minX;
    if (offset !== 0) {
      for (const id of Object.keys(pos)) pos[id].x += offset;
      bounds.minX += offset;
      bounds.maxX += offset;
    }

    // SVG curves connecting each parent to each visible child
    const paths = [];
    for (const id of Object.keys(pos)) {
      const parentPos = pos[id];
      if (isCollapsed(id)) continue;
      for (const kid of childrenOf(id)) {
        if (!pos[kid.id]) continue;
        const px = parentPos.x + CARD_W / 2;
        const py = parentPos.y + CARD_H;        // card bottom
        const cx = pos[kid.id].x + CARD_W / 2;
        const cy = pos[kid.id].y;                // card top
        const midY = py + (cy - py) / 2;
        paths.push({
          id: `${id}-${kid.id}`,
          d: `M ${px.toFixed(1)} ${py.toFixed(1)} C ${px.toFixed(1)} ${midY.toFixed(1)}, ${cx.toFixed(1)} ${midY.toFixed(1)}, ${cx.toFixed(1)} ${cy.toFixed(1)}`,
        });
      }
    }

    return {
      pos,
      personMap,
      paths,
      width: Math.ceil(bounds.maxX + 60),
      height: Math.ceil(bounds.maxY + 60),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, childrenOf, isCollapsed, reportCount]);

  // Position a specific card in the visible canvas area. `anchor` controls
  // where vertically:
  //   'top'    — card lands near the top of the viewport (root view)
  //   'center' — card lands in the middle of the viewport (search jump)
  const centerOnElement = (el, opts = {}) => {
    const { anchor = 'center', targetZoom } = opts;
    const c = canvasRef.current; if (!c || !el) return;
    const cr = c.getBoundingClientRect();
    if (cr.width === 0 || cr.height === 0) return;
    const er = el.getBoundingClientRect();
    const z = targetZoom != null ? targetZoom : zoom;
    const cardCx = er.left + er.width / 2;
    const cardCy = anchor === 'top' ? er.top : er.top + er.height / 2;
    const targetCx = cr.left + cr.width / 2;
    const targetCy = anchor === 'top' ? cr.top + 60 : cr.top + cr.height / 2;
    if (z !== zoom) setZoom(z);
    setPan(prev => ({
      x: prev.x + (targetCx - cardCx),
      y: prev.y + (targetCy - cardCy),
    }));
  };

  // Initial center: drop the first root (e.g., Seb) near the top of the
  // viewport. Important: target the ROOT's card specifically rather than
  // the first card in DOM order — with absolute positioning, the first
  // DOM card is the leftmost leaf, NOT the root.
  const initialCenteredRef = useRef(false);
  useEffect(() => {
    if (initialCenteredRef.current) return;
    if (!innerRef.current || !canvasRef.current) return;
    if (!roots || roots.length === 0) return;
    const rootId = roots[0].id;
    const t = setTimeout(() => {
      const inner = innerRef.current; const canvas = canvasRef.current;
      if (!inner || !canvas) return;
      const rootWrap = inner.querySelector(`[data-person-id="${rootId}"]`);
      if (!rootWrap) return;
      if (canvas.getBoundingClientRect().width === 0) return;
      centerOnElement(rootWrap, { anchor: 'top', targetZoom: 1 });
      initialCenteredRef.current = true;
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  // Center on demand (from search picks or expand actions)
  useEffect(() => {
    if (!centerTarget) return;
    const t = setTimeout(() => {
      const inner = innerRef.current; if (!inner) return;
      const el = inner.querySelector(`[data-person-id="${centerTarget.id}"]`);
      if (el) centerOnElement(el, { anchor: centerTarget.anchor || 'center', targetZoom: 1 });
      onCenterDone?.();
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerTarget]);

  const ZOOM_MIN = 0.25;
  const ZOOM_MAX = 2;

  const zoomBy = (factor, originX, originY) => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const ox = originX ?? rect.width / 2;
    const oy = originY ?? rect.height / 2;
    setZoom(prevZ => {
      const newZ = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prevZ * factor));
      const realFactor = newZ / prevZ;
      setPan(prevPan => ({
        x: ox - (ox - prevPan.x) * realFactor,
        y: oy - (oy - prevPan.y) * realFactor,
      }));
      return newZ;
    });
  };

  const onWheel = (e) => {
    // Treat any wheel inside the canvas as zoom. Direction: scroll up = zoom in.
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const ox = e.clientX - rect.left;
    const oy = e.clientY - rect.top;
    // Smoother: small factor per tick. Larger for ctrl/cmd pinch.
    const intensity = (e.ctrlKey || e.metaKey) ? 0.01 : 0.0015;
    const factor = Math.exp(-e.deltaY * intensity);
    zoomBy(factor, ox, oy);
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    // Don't start a pan if clicking on a card / button / link
    if (e.target.closest('.org-card, button, a, input, .org-card-badge')) return;
    e.preventDefault();
    setPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };

  const onMouseMove = (e) => {
    if (!panning || !panStartRef.current) return;
    const s = panStartRef.current;
    setPan({ x: s.panX + (e.clientX - s.x), y: s.panY + (e.clientY - s.y) });
  };

  const onMouseUp = () => {
    setPanning(false);
    panStartRef.current = null;
  };

  const resetView = () => {
    if (!canvasRef.current || !innerRef.current) return;
    const c = canvasRef.current; const i = innerRef.current;
    setZoom(1);
    setPan({ x: Math.max(20, (c.clientWidth - i.scrollWidth) / 2), y: 40 });
  };

  // Bind wheel/mouse listeners on canvas with non-passive wheel
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style>{ORG_TREE_CSS}</style>
      <div
        ref={canvasRef}
        className={`org-canvas${panning ? ' is-panning' : ''}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div
          ref={innerRef}
          className="org-canvas-inner"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            width: layout.width,
            height: layout.height,
          }}
        >
          <svg
            width={layout.width}
            height={layout.height}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          >
            <g fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round">
              {layout.paths.map(p => <path key={p.id} d={p.d} />)}
            </g>
          </svg>
          {Object.entries(layout.pos).map(([id, p]) => {
            const person = layout.personMap[id];
            if (!person) return null;
            const count = reportCount[id] || 0;
            const collapsed = isCollapsed(id);
            return (
              <div
                key={id}
                data-person-id={id}
                style={{
                  position: 'absolute',
                  left: p.x,
                  top: p.y,
                  width: CARD_W,
                }}
              >
                <OrgCard
                  person={person}
                  isAdmin={isAdmin}
                  onEdit={onEdit}
                  highlight={searchMatches?.has(id)}
                />
                {count > 0 && (
                  <button
                    type="button"
                    className={`org-toggle-btn${collapsed ? ' is-collapsed' : ''}`}
                    onClick={() => toggleCollapsed(id)}
                    title={`${count} direct ${count === 1 ? 'report' : 'reports'} — click to ${collapsed ? 'expand' : 'collapse'}`}
                  >
                    {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="org-zoom-toolbar">
          <button type="button" onClick={() => zoomBy(0.85)} title="Zoom out (or scroll wheel)">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => zoomBy(1 / 0.85)} title="Zoom in (or scroll wheel)">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={resetView} title="Reset / fit to view">
            <Crosshair className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </div>
    </>
  );
}

function OrgTreeNode({ person, siblings, childrenOf, reportCount, isCollapsed, toggleCollapsed, isAdmin, onEdit, searchMatches }) {
  const kids = childrenOf(person.id);
  const count = reportCount[person.id] || 0;
  const collapsed = isCollapsed(person.id);
  const isMatch = searchMatches?.has(person.id);
  const showChildren = !collapsed && kids.length > 0;
  const liClasses = [
    count === 0 ? 'org-leaf' : '',
    siblings === 1 ? 'org-only-child' : '',
  ].filter(Boolean).join(' ');
  const wrapClasses = [
    'org-tree-card-wrap',
    count > 0 ? 'has-children' : '',
    collapsed ? 'is-collapsed' : '',
  ].filter(Boolean).join(' ');

  return (
    <li className={liClasses}>
      <div className={wrapClasses} data-person-id={person.id}>
        <OrgCard
          person={person}
          directReports={count}
          isAdmin={isAdmin}
          onEdit={onEdit}
          highlight={isMatch}
        />
        {count > 0 && (
          <button
            type="button"
            className={`org-toggle-btn${collapsed ? ' is-collapsed' : ''}`}
            onClick={() => toggleCollapsed(person.id)}
            title={`${count} direct ${count === 1 ? 'report' : 'reports'} — click to ${collapsed ? 'expand' : 'collapse'}`}
            aria-label={collapsed ? `Expand ${count} reports` : `Collapse ${count} reports`}
          >
            {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {showChildren && (
        <ul>
          {kids.map(c => (
            <OrgTreeNode
              key={c.id}
              person={c}
              siblings={kids.length}
              childrenOf={childrenOf}
              reportCount={reportCount}
              isCollapsed={isCollapsed}
              toggleCollapsed={toggleCollapsed}
              isAdmin={isAdmin}
              onEdit={onEdit}
              searchMatches={searchMatches}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// SearchBox — input + dropdown of matches. Single click on a match focuses.
// ---------------------------------------------------------------------------
function SearchBox({ search, setSearch, results, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const shown = results.slice(0, 12);
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5" style={{ color: '#94A3B8' }} />
      <input
        type="text"
        value={search}
        onFocus={() => setOpen(true)}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && shown.length > 0) {
            onPick(shown[0]); setOpen(false);
          } else if (e.key === 'Escape') {
            setSearch(''); setOpen(false);
          }
        }}
        placeholder="Search name, title, dept, city…"
        className="h-8 text-[13px] rounded-md pl-7 pr-3 focus:outline-none"
        style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A', width: 240 }}
      />
      {open && search.trim().length > 0 && (
        <div
          style={{
            position: 'absolute', top: 36, left: 0, width: 320,
            background: '#fff', border: '1px solid #E5E9F0', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            maxHeight: 360, overflowY: 'auto', zIndex: 10,
          }}
        >
          {shown.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 12, color: '#94A3B8' }}>No matches</div>
          ) : (
            <>
              <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8', borderBottom: '1px solid #F0F2F6' }}>
                {results.length === 1 ? '1 match' : `${results.length} matches${results.length > shown.length ? ` (showing ${shown.length})` : ''}`}
              </div>
              {shown.map(p => {
                const disp = displayName(p.name);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onPick(p); setOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 12px',
                      background: 'none', border: 'none',
                      borderBottom: '1px solid #F7F8FA',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F0F4FA'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{
                      flexShrink: 0, width: 28, height: 28, borderRadius: 14,
                      backgroundColor: colorFor(p.name), color: textColorFor(p.name),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 600,
                    }}>{initialsOf(disp)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{disp}</div>
                      <div style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {[p.title, [p.state, p.city].filter(Boolean).join(' · ')].filter(Boolean).join(' — ')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OrgCard({ person, isAdmin, onEdit, highlight }) {
  const hasPhoto = !!person.photo_url;
  const avatarStyle = hasPhoto
    ? { backgroundImage: `url("${person.photo_url}")`, backgroundColor: '#F0F2F6', color: 'transparent' }
    : { backgroundColor: colorFor(person.name), color: textColorFor(person.name) };

  const cls = ['org-card'];
  if (highlight) cls.push('is-match');

  const display = displayName(person.name);
  const locStr = [person.state, person.city].filter(Boolean).join(' · ');
  const fullTooltip = [display, person.title, person.department, locStr, person.email, person.phone].filter(Boolean).join(' · ');
  return (
    <div className={cls.join(' ')} title={fullTooltip}>
      <div className="org-avatar" style={avatarStyle}>
        {!hasPhoto && initialsOf(display)}
      </div>
      <div className="org-card-body">
        <div className="org-card-name">{display}</div>
        {person.title && <div className="org-card-title">{person.title}</div>}
        {(person.department || locStr) && (
          <div className="org-card-meta">
            {[person.department, locStr].filter(Boolean).join(' · ')}
          </div>
        )}
        {(person.email || person.phone) && (
          <div className="org-card-meta" style={{ fontStyle: 'normal', color: '#1A3665', marginTop: 2 }}>
            {person.email && (
              <a
                href={`mailto:${person.email}`}
                onClick={e => e.stopPropagation()}
                style={{ color: '#1A3665', textDecoration: 'none' }}
                title={person.email}
              >
                {person.email}
              </a>
            )}
            {person.email && person.phone && <span style={{ color: '#CBD5E1' }}> · </span>}
            {person.phone && (
              <a
                href={`tel:${person.phone.replace(/[^0-9+]/g, '')}`}
                onClick={e => e.stopPropagation()}
                style={{ color: '#1A3665', textDecoration: 'none' }}
              >
                {person.phone}
              </a>
            )}
          </div>
        )}
      </div>
      {isAdmin && (
        <button
          type="button"
          className="org-edit-btn"
          onClick={() => onEdit(person)}
          title="Edit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / edit / delete modal
// ---------------------------------------------------------------------------
function EditPersonModal({ organization, person, people, onClose, onSaved }) {
  const isNew = !person;
  const [form, setForm] = useState(() => ({
    name:        person?.name        || '',
    title:       person?.title       || '',
    email:       person?.email       || '',
    phone:       person?.phone       || '',
    department:  person?.department  || '',
    state:       person?.state       || '',
    city:        person?.city        || '',
    photo_url:   person?.photo_url   || '',
    manager_id:  person?.manager_id  || '',
    sort_order:  person?.sort_order  ?? 0,
    notes:       person?.notes       || '',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Anyone except self + own descendants is a valid manager option.
  const validManagers = useMemo(() => {
    if (!person) return people;
    // BFS down from person.id to find all descendants
    const banned = new Set([person.id]);
    let frontier = [person.id];
    while (frontier.length) {
      const next = [];
      for (const id of frontier) {
        for (const p of people) {
          if (p.manager_id === id && !banned.has(p.id)) {
            banned.add(p.id); next.push(p.id);
          }
        }
      }
      frontier = next;
    }
    return people.filter(p => !banned.has(p.id));
  }, [person, people]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const stateVal = form.state.trim().toUpperCase() || null;
      const cityVal = form.city.trim() || null;
      const locationVal = stateVal && cityVal ? `${stateVal} - ${cityVal}` : (stateVal || cityVal);
      const patch = {
        name: form.name.trim(),
        title: form.title.trim() || null,
        email: form.email.trim().toLowerCase() || null,
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
        state: stateVal,
        city: cityVal,
        location: locationVal,
        photo_url: form.photo_url.trim() || null,
        manager_id: form.manager_id || null,
        sort_order: Number(form.sort_order) || 0,
        notes: form.notes.trim() || null,
      };
      if (!patch.name) throw new Error('Name is required');
      if (isNew) {
        const { error } = await supabase.from('org_chart_people').insert({
          organization_id: organization.id, ...patch,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('org_chart_people').update(patch).eq('id', person.id);
        if (error) throw error;
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${displayName(person.name)} from the org chart? Their direct reports will become unassigned (manager set to null).`)) return;
    setSaving(true); setErr(null);
    try {
      const { error } = await supabase.from('org_chart_people').delete().eq('id', person.id);
      if (error) throw error;
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <h2 className="text-base font-semibold" style={{ color: '#0F172A' }}>{isNew ? 'Add person' : 'Edit person'}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 cursor-pointer" style={{ color: '#64748B' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {err && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>{err}</div>
          )}
          <Field label="Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} />
          <Field label="Title" value={form.title} onChange={v => setForm({ ...form, title: v })} />
          <Field label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} type="email" />
          <Field label="Cell / Phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Department" value={form.department} onChange={v => setForm({ ...form, department: v })} />
            <Field label="State (2-letter or blank for Remote)" value={form.state} onChange={v => setForm({ ...form, state: v })} />
            <Field label="City (or 'Remote')" value={form.city} onChange={v => setForm({ ...form, city: v })} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Reports to</label>
            <select
              value={form.manager_id || ''}
              onChange={e => setForm({ ...form, manager_id: e.target.value })}
              className="w-full h-9 text-[13px] rounded-md px-2 mt-1 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
            >
              <option value="">— top level —</option>
              {validManagers.map(p => (
                <option key={p.id} value={p.id}>{displayName(p.name)}{p.title ? ` (${p.title})` : ''}</option>
              ))}
            </select>
          </div>
          <Field label="Photo URL" value={form.photo_url} onChange={v => setForm({ ...form, photo_url: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order" value={form.sort_order} onChange={v => setForm({ ...form, sort_order: v })} type="number" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full text-[13px] rounded-md px-2 py-1.5 mt-1 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A', minHeight: 60 }}
            />
          </div>
        </div>
        <div className="px-5 py-3 flex items-center justify-between gap-2" style={{ borderTop: '1px solid #F0F2F6' }}>
          {!isNew ? (
            <button type="button" onClick={remove} disabled={saving}
              className="px-3 py-1.5 text-[12px] font-semibold rounded-md flex items-center gap-1.5 hover:opacity-80 cursor-pointer"
              style={{ backgroundColor: 'white', color: '#B91C1C', border: '1px solid #FECACA' }}>
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold rounded-md cursor-pointer"
                    style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}>Cancel</button>
            <button type="button" onClick={save} disabled={saving}
                    className="px-3 py-1.5 text-[12px] font-semibold rounded-md cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: '#1A3665', color: 'white' }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (isNew ? 'Add' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full h-9 text-[13px] rounded-md px-2 mt-1 focus:outline-none"
        style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import CSV modal
// ---------------------------------------------------------------------------
function ImportCsvModal({ organization, onClose, onComplete }) {
  const [rows, setRows] = useState([]);     // raw parsed rows (pre-normalize)
  const [normalized, setNormalized] = useState([]);  // post-alias + title-case
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    setErr(null); setResult(null);
    if (!file) return;
    setFileName(file.name);
    try {
      const isXlsx = /\.xlsx?$/i.test(file.name) || file.type.includes('sheet') || file.type.includes('excel');
      const parsed = isXlsx
        ? await parseXlsx(file)
        : parseCsv(await file.text());
      if (!parsed.length) throw new Error('File has no data rows.');
      const norm = parsed.map(normalizeRow);
      if (!norm.some(r => r.name)) {
        throw new Error('No "name" column detected. Expected one of: name, legal name, full name, employee name.');
      }
      setRows(parsed);
      setNormalized(norm);
    } catch (e) {
      setErr(e.message); setRows([]); setNormalized([]);
    }
  };

  const runImport = async () => {
    setImporting(true); setErr(null);
    try {
      const { data, error } = await supabase.rpc('bulk_import_org_chart', {
        p_org_id: organization.id,
        p_rows: normalized,
      });
      if (error) throw error;
      setResult(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <h2 className="text-base font-semibold" style={{ color: '#0F172A' }}>Import org chart from spreadsheet</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 cursor-pointer" style={{ color: '#64748B' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="rounded-lg px-3 py-2 text-[12px] leading-relaxed" style={{ backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985' }}>
            <strong>Accepts .xlsx and .csv.</strong> Common HR headers are auto-detected — no need to rename columns first.
            <br />
            Recognized: <code>Legal Name</code> / <code>Name</code> (required), <code>Job Title</code>, <code>Email</code>, <code>Category</code> / <code>Employment Type</code>, <code>Business Unit</code> / <code>Department</code>, <code>Location</code>, <code>Reports To</code> / <code>Reports To Legal Name</code>, <code>Manager Email</code>, <code>Photo URL</code>.
            <br />
            <strong>Category</strong> is stored as-is. Use the <strong>Hide part-time</strong> toggle in the toolbar to filter the view non-destructively.
            <br />
            <strong>Manager linking:</strong> matches <code>Reports To</code> name against the imported <code>Name</code> column (falls back to email if both have one). <strong>Title-casing:</strong> all text fields are normalized to Title Case automatically (handles Mc/Mac prefixes, roman numerals, acronyms). Idempotent — re-importing the same roster updates rather than duplicates.
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={e => onFile(e.target.files?.[0])}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full py-8 rounded-lg flex flex-col items-center gap-2 cursor-pointer hover:opacity-80"
            style={{ backgroundColor: '#FAFBFD', border: '2px dashed #E5E9F0', color: '#64748B' }}
          >
            <Upload className="w-5 h-5" />
            <span className="text-[13px] font-semibold">{fileName || 'Choose a CSV or XLSX file'}</span>
            {normalized.length > 0 && <span className="text-[11px]">{normalized.length} row{normalized.length === 1 ? '' : 's'} parsed and normalized</span>}
          </button>

          {err && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>{err}</div>
          )}

          {result && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
              Import complete · <strong>{result.inserted}</strong> inserted · <strong>{result.updated}</strong> updated · <strong>{result.managers_linked}</strong> manager links resolved
              {result.skipped > 0 && <> · {result.skipped} skipped</>}
            </div>
          )}

          {normalized.length > 0 && !result && (() => {
            // Show only fields that actually have data in the preview rows
            const previewRows = normalized.slice(0, 5);
            const seen = new Set();
            for (const r of normalized) for (const k of Object.keys(r)) seen.add(k);
            // Canonical column order
            const order = ['name','title','email','category','department','location','manager_name','manager_email','photo_url'];
            const cols = order.filter(c => seen.has(c));
            return (
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #F0F2F6' }}>
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider flex items-center justify-between" style={{ backgroundColor: '#FAFBFD', color: '#64748B' }}>
                  <span>Preview (first 5 of {normalized.length}, after title-case + header mapping)</span>
                </div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr style={{ backgroundColor: '#FAFBFD' }}>
                      {cols.map(h => (
                        <th key={h} className="text-left px-2 py-1 font-semibold" style={{ color: '#64748B' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #F0F2F6' }}>
                        {cols.map(h => (
                          <td key={h} className="px-2 py-1" style={{ color: '#475569' }}>{r[h] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid #F0F2F6' }}>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold rounded-md cursor-pointer"
                  style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {normalized.length > 0 && !result && (
            <button type="button" onClick={runImport} disabled={importing}
                    className="px-3 py-1.5 text-[12px] font-semibold rounded-md cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: '#1A3665', color: 'white' }}>
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : `Import ${normalized.length} ${normalized.length === 1 ? 'row' : 'rows'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
